import { memo, useMemo } from 'react';

import { TextButton } from '@hcs/design-system';
import { CardTabs, CardTabsProps } from '@hcs/design-system';
import { PlusIcon } from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';

import { CHART_TAB_IDS } from '../../constants/cerberusStats.constants';
import { MACRO_INSIGHTS_FROM_DATE } from '../../constants/marketInsights.constants';
import { MsaHpiChart } from '../../features/MsaHpiChart';
import { MsaListingStatsChart } from '../../features/MsaListingStatsChart';
import { MsaRpiChart } from '../../features/MsaRpiChart';
import { MsaUnemploymentChart } from '../../features/MsaUnemploymentChart';
import { useCerberusStatsFields } from '../../hooks/useCerberusStatsFields';
import { useMsaListingStatsTimeSeries } from '../../hooks/useMsaListingStatsTimeSeries';
import {
  MARKET_STATS_FOR_USER_GEO_TYPE,
  MarketStateChartsForUserLauncher,
} from '../MarketStateChartsForUser';
import { GeoTypeChartDropdownMobile } from '../ZipMsaChartDropdownMobile';

import styles from './MsaCharts.module.css';

interface MsaChartsProps {
  className?: string;
  msaId: string;
  isNarrowView?: boolean;
}

const dataHcName = 'msa-charts';
export const MsaCharts = memo(
  ({ className, msaId, isNarrowView }: MsaChartsProps) => {
    const { isPhoneWidth } = useScreenSize();
    const { data: listingStatsTS, isLoading: isLoadingTSData } =
      useMsaListingStatsTimeSeries({
        msaId,
        fromDate: MACRO_INSIGHTS_FROM_DATE,
      });
    const { data: customFieldsData, isLoading: isLoadingCustomFields } =
      useCerberusStatsFields('chartsMsa');

    const tabs: CardTabsProps['tabs'] = useMemo(() => {
      const MacroInsightsTab = {
        tabId: CHART_TAB_IDS.MacroInsights,
        label: <>Macro Insights</>,
        content: (
          <div className={styles.ChartsTabContent}>
            <MsaHpiChart msaId={msaId} className={styles.ChartSection} />
            <MsaRpiChart msaId={msaId} className={styles.ChartSection} />
            <MsaUnemploymentChart
              msaId={msaId}
              className={styles.ChartSection}
            />
          </div>
        ),
      };

      if (isLoadingTSData || isLoadingCustomFields) {
        return [MacroInsightsTab];
      }

      const hasMarketActivityData =
        listingStatsTS != null && listingStatsTS.saleListingStats.length > 0;

      if (hasMarketActivityData) {
        const MarketActivityTab = {
          tabId: CHART_TAB_IDS.MarketActivity,
          label: <>Market Activity</>,
          content: (
            <div className={styles.ChartsTabContent}>
              {customFieldsData?.order.map((cerberusField) => {
                return (
                  <MsaListingStatsChart
                    key={cerberusField}
                    className={styles.ChartSection}
                    cerberusStatsField={cerberusField}
                    msaId={msaId}
                  />
                );
              })}
            </div>
          ),
        };
        return [MarketActivityTab, MacroInsightsTab];
      } else {
        return [MacroInsightsTab];
      }
    }, [
      customFieldsData,
      isLoadingTSData,
      isLoadingCustomFields,
      msaId,
      listingStatsTS,
    ]);
    const chooseFieldsContent = (
      <MarketStateChartsForUserLauncher
        showTooltip={false}
        geoType={MARKET_STATS_FOR_USER_GEO_TYPE.MSA}
        trigger={
          <TextButton
            dataHcName={`${dataHcName}-data-priority`}
            icon={<PlusIcon size="sm" />}
            noPadding
          >
            Choose Fields
          </TextButton>
        }
      />
    );
    return isPhoneWidth || isNarrowView ? (
      <GeoTypeChartDropdownMobile
        chartSelector={chooseFieldsContent}
        market={tabs.length === 2 ? tabs[0]?.content : undefined}
        macro={tabs.length === 1 ? tabs[0]?.content : tabs[1]?.content}
      />
    ) : (
      <CardTabs
        searchParamId={dataHcName}
        dataHcName={dataHcName}
        className={className}
        tabs={tabs}
        actionTextConfig={{
          tabId: CHART_TAB_IDS.MarketActivity,
          content: (
            <div className={styles.DataPriorityControlContainer}>
              {chooseFieldsContent}
            </div>
          ),
        }}
        active={
          tabs.length > 1
            ? CHART_TAB_IDS.MarketActivity
            : CHART_TAB_IDS.MacroInsights
        }
      />
    );
  },
);
